// COP — "tracks" capability (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// A pure CONTRIBUTE-ONLY capability (Increment 3 / the sole-writer closure). It touches
// ONLY HostContext + the contract types — NO host internals (no pdp.ts, no applyGate) and
// NO engine writes (CapabilityEngine has no write surface). On activate it declares ONE
// scatter layer for track/alert/observation points via ctx.registerLayer; the HOST creates
// the GPU layer (host-mediated registerLayer) and the HOST paints every cleared-visible
// envelope of those kinds (cop.ts's per-publish painter + the projection's subject re-gate).
// The capability never enters the render loop, holds no React, and never writes the engine.
//
// (Before Increment 3 this capability CHEATED — it imported applyGate/CopPdp from the host
// and called ctx.engine.upsertEntities directly. Closing the bypass made it as pure as the
// dogfood multiIntCapability; the static no-bypass gate now enforces that purity.)

import type {
  Capability,
  HostContext,
  Disposable,
  LayerSpec,
} from "../../../../packages/contracts-ts/src/index.ts";

/** Kinds this capability's layer draws on the map. ("geometry" = operator-authored
 *  waypoint markers from markerCapability — they ride the same ScatterplotLayer.) */
const RENDERED_KINDS = ["track", "alert", "observation", "geometry"] as const;

/** The deck layer this capability owns; ScatterplotLayer is a render-engine
 *  LayerManager-supported type, so registerLayer accepts it. */
const LAYER_ID = "scop:layer:tracks";

const TRACKS_LAYER: LayerSpec = {
  id: LAYER_ID,
  kinds: [...RENDERED_KINDS],
  type: "ScatterplotLayer",
  props: { radiusMeters: 9_000 },
  updateTriggers: { getFillColor: "affiliation" },
};

export function createTracksCapability(): Capability {
  let disposables: Disposable[] = [];

  return {
    id: "scop:cap:tracks",

    activate(ctx: HostContext): void {
      // CONTRIBUTE the in-map layer. registerLayer is host-mediated: the host creates the
      // GPU layer and paints the cleared-visible track/alert/observation envelopes itself.
      // The capability adds nothing else — no subscribe, no paint, no PDP.
      disposables = [ctx.registerLayer(TRACKS_LAYER)];
    },

    deactivate(): void {
      for (const d of disposables.splice(0)) {
        try {
          d.dispose();
        } catch {
          // teardown is best-effort
        }
      }
    },
  };
}
